import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, Search, Package, Layers, MapPin, AlertTriangle, ArrowLeftRight } from 'lucide-react';

export default function Inventory() {
  const [tab, setTab] = useState('components');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({});

  const tabs = [
    { key: 'components', label: 'Component Register', icon: Layers },
    { key: 'parts', label: 'Parts Catalogue', icon: Package },
    { key: 'stock', label: 'Stock Management', icon: MapPin },
    { key: 'locations', label: 'Locations', icon: MapPin },
    { key: 'critical', label: 'Critical Spares', icon: AlertTriangle },
    { key: 'defects', label: 'Defects', icon: AlertTriangle },
    { key: 'transfers', label: 'Stock Transfers', icon: ArrowLeftRight },
    { key: 'running-hours', label: 'Running Hours', icon: Layers },
  ];

  useEffect(() => { loadData(); }, [tab, filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      let res;
      switch (tab) {
        case 'components': res = await api.get('/inventory/components/tree'); break;
        case 'parts': res = await api.get('/inventory/parts'); break;
        case 'stock': res = await api.get('/inventory/stock'); break;
        case 'locations': res = await api.get('/inventory/locations'); break;
        case 'critical': res = await api.get('/inventory/critical-spares'); break;
        case 'defects': res = await api.get('/inventory/defects'); break;
        case 'transfers': res = await api.get('/inventory/transfers'); break;
        case 'running-hours': res = await api.get('/inventory/running-hours'); break;
        default: res = await api.get('/inventory/components');
      }
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="tabs">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <div key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              <Icon size={14} />{t.label}
            </div>
          );
        })}
      </div>

      {tab === 'components' && <ComponentsTab data={data} loading={loading} />}
      {tab === 'parts' && <PartsTab data={data} loading={loading} onRefresh={loadData} />}
      {tab === 'stock' && <StockTab data={data} loading={loading} onRefresh={loadData} />}
      {tab === 'locations' && <LocationsTab data={data} loading={loading} onRefresh={loadData} />}
      {tab === 'critical' && <CriticalSparesTab data={data} loading={loading} />}
      {tab === 'defects' && <DefectsTab data={data} loading={loading} onRefresh={loadData} />}
      {tab === 'transfers' && <TransfersTab data={data} loading={loading} onRefresh={loadData} />}
      {tab === 'running-hours' && <RunningHoursTab data={data} loading={loading} onRefresh={loadData} />}
    </div>
  );
}

// Component Register with Tree View
function ComponentsTab({ data, loading }) {
  const [expanded, setExpanded] = useState({});
  const [flatData, setFlatData] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ department: '', criticality: '' });
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    api.get('/inventory/components', { params: { search, ...filter } }).then(r => setFlatData(r.data)).catch(console.error);
  }, [search, filter]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const TreeNode = ({ node, depth = 0 }) => {
    const hasChildren = node.children?.length > 0;
    const isExpanded = expanded[node.id];
    return (
      <div className="tree-item">
        <div className="tree-row" onClick={() => hasChildren && toggle(node.id)} style={{ paddingLeft: depth * 20 + 8 }}>
          {hasChildren ? (isExpanded ? <span style={{ fontSize: 12 }}>▼</span> : <span style={{ fontSize: 12 }}>▶</span>) : <span style={{ width: 12 }}></span>}
          <Layers size={14} />
          <span style={{ fontWeight: node.parent_id === null ? 600 : 400 }}>{node.component_name}</span>
          <span className={`badge badge-${node.criticality?.toLowerCase()}`} style={{ marginLeft: 8, fontSize: 10 }}>{node.criticality}</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-secondary)' }}>
            {node.running_hours > 0 ? `${node.running_hours.toLocaleString()} hrs` : ''}
          </span>
        </div>
        {hasChildren && isExpanded && node.children.map(child => <TreeNode key={child.id} node={child} depth={depth + 1} />)}
      </div>
    );
  };

  return (
    <div>
      <div className="action-bar">
        <input className="form-input" style={{ maxWidth: 300 }} placeholder="Search components..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-select" style={{ minWidth: 130 }} value={filter.department} onChange={e => setFilter({ ...filter, department: e.target.value })}>
            <option value="">All Depts</option>
            <option value="Engine">Engine</option>
            <option value="Deck">Deck</option>
            <option value="Electrical">Electrical</option>
            <option value="Safety">Safety</option>
          </select>
          <button className="btn btn-accent" onClick={() => setShowCreate(true)}><Plus size={15} /> Add Component</button>
        </div>
      </div>
      <div className="card">
        <div className="card-body" style={{ maxHeight: 600, overflowY: 'auto' }}>
          {loading ? <div className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div> :
           data.length === 0 ? <div className="empty-state"><Layers /><h4>No components found</h4></div> :
           data.map(node => <TreeNode key={node.id} node={node} />)
          }
        </div>
      </div>
      {showCreate && <CreateComponentModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); api.get('/inventory/components').then(r => setFlatData(r.data)); }} />}
    </div>
  );
}

function CreateComponentModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ component_name: '', department: 'Engine', criticality: 'Essential', maker: '', model_type: '', serial_number: '' });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/inventory/components', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Add Component</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Component Name *</label><input className="form-input" value={form.component_name} onChange={e => setForm({ ...form, component_name: e.target.value })} required /></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Department</label><select className="form-select" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
                {['Engine','Deck','Electrical','Safety','Hull'].map(d => <option key={d}>{d}</option>)}
              </select></div>
              <div className="form-group"><label className="form-label">Criticality</label><select className="form-select" value={form.criticality} onChange={e => setForm({ ...form, criticality: e.target.value })}>
                {['Critical','Essential','Non-Essential'].map(c => <option key={c}>{c}</option>)}
              </select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Maker</label><input className="form-input" value={form.maker} onChange={e => setForm({ ...form, maker: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Model / Type</label><input className="form-input" value={form.model_type} onChange={e => setForm({ ...form, model_type: e.target.value })} /></div>
            </div>
            <div className="form-group"><label className="form-label">Serial Number</label><input className="form-input" value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Parts Catalogue
function PartsTab({ data, loading, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const filtered = data.filter(p => !search || p.part_name?.toLowerCase().includes(search.toLowerCase()) || p.part_number?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="action-bar">
        <input className="form-input" style={{ maxWidth: 300 }} placeholder="Search parts..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            <option value="Spare Part">Spare Part</option>
            <option value="Consumable">Consumable</option>
            <option value="Chemical">Chemical</option>
            <option value="Tool">Tool</option>
            <option value="Safety">Safety</option>
          </select>
          <button className="btn btn-accent" onClick={() => setShowCreate(true)}><Plus size={15} /> Add Part</button>
        </div>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(5)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Part #</th><th>Part Name</th><th>Type</th><th>Unit</th><th>Maker</th><th>Critical</th><th>MSDS</th></tr></thead>
             <tbody>
               {filtered.filter(p => !typeFilter || p.part_type === typeFilter).map(p => (
                 <tr key={p.id}>
                   <td><strong>{p.part_number}</strong></td>
                   <td>{p.part_name}</td>
                   <td><span className="badge badge-draft">{p.part_type}</span></td>
                   <td>{p.unit_of_measure}</td>
                   <td>{p.maker_name || '—'}</td>
                   <td>{p.is_critical_spare ? <span className="badge badge-critical">Yes</span> : '—'}</td>
                   <td>{p.msds_required ? <span className="badge badge-warning">Required</span> : '—'}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreatePartModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); onRefresh(); }} />}
    </div>
  );
}

function CreatePartModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ part_number: '', part_name: '', description: '', part_type: 'Spare Part', unit_of_measure: 'Pieces', maker_name: '', impa_issacode: '', is_critical_spare: false, msds_required: false });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/inventory/parts', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Add Part to Catalogue</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group"><label className="form-label">Part Number *</label><input className="form-input" value={form.part_number} onChange={e => setForm({ ...form, part_number: e.target.value })} required /></div>
              <div className="form-group"><label className="form-label">Part Name *</label><input className="form-input" value={form.part_name} onChange={e => setForm({ ...form, part_name: e.target.value })} required /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Type *</label><select className="form-select" value={form.part_type} onChange={e => setForm({ ...form, part_type: e.target.value })}>
                {['Spare Part','Consumable','Chemical','Tool','Safety'].map(t => <option key={t}>{t}</option>)}
              </select></div>
              <div className="form-group"><label className="form-label">Unit *</label><select className="form-select" value={form.unit_of_measure} onChange={e => setForm({ ...form, unit_of_measure: e.target.value })}>
                {['Pieces','Litres','Kg','Metres','Sets'].map(u => <option key={u}>{u}</option>)}
              </select></div>
            </div>
            <div className="form-group"><label className="form-label">Maker</label><input className="form-input" value={form.maker_name} onChange={e => setForm({ ...form, maker_name: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div className="form-row">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}><input type="checkbox" checked={form.is_critical_spare} onChange={e => setForm({ ...form, is_critical_spare: e.target.checked })} /> Critical Spare</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}><input type="checkbox" checked={form.msds_required} onChange={e => setForm({ ...form, msds_required: e.target.checked })} /> MSDS Required</label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Stock Management
function StockTab({ data, loading, onRefresh }) {
  const [filter, setFilter] = useState('');
  const filtered = data.filter(s => !filter || (filter === 'low' && s.current_quantity <= s.reorder_point) || (filter === 'critical' && s.current_quantity <= s.minimum_stock * 0.5));

  return (
    <div>
      <div className="action-bar">
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ k: '', l: 'All' }, { k: 'low', l: 'Below Reorder' }, { k: 'critical', l: 'Critical Low' }].map(f => (
            <button key={f.k} className={`btn btn-sm ${filter === f.k ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilter(f.k)}>{f.l}</button>
          ))}
        </div>
        <button className="btn btn-accent btn-sm" onClick={async () => { await api.post('/ai/suggest-requisitions'); alert('Requisition suggestions generated'); }}>AI Suggest Requisitions</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(5)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Part #</th><th>Part Name</th><th>Type</th><th>Location</th><th>Current</th><th>Min</th><th>Reorder</th><th>Status</th></tr></thead>
             <tbody>
               {filtered.map(s => {
                 const status = s.current_quantity <= 0 ? 'Zero' : s.current_quantity <= s.minimum_stock ? 'Low' : s.current_quantity <= s.reorder_point ? 'Reorder' : 'OK';
                 return (
                   <tr key={s.id}>
                     <td><strong>{s.part_number}</strong></td>
                     <td>{s.part_name}</td>
                     <td><span className="badge badge-draft">{s.part_type}</span></td>
                     <td>{s.location_name || '—'}</td>
                     <td style={{ fontWeight: 600, color: status === 'Zero' ? 'var(--danger)' : status === 'Low' ? 'var(--warning)' : 'var(--text)' }}>{s.current_quantity}</td>
                     <td>{s.minimum_stock}</td>
                     <td>{s.reorder_point}</td>
                     <td><span className={`badge ${status === 'Zero' ? 'badge-critical' : status === 'Low' ? 'badge-warning' : 'badge-healthy'}`}>{status}</span></td>
                   </tr>
                 );
               })}
             </tbody>
           </table>
         </div>
        }
      </div>
    </div>
  );
}

// Locations
function LocationsTab({ data, loading, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Define physical storage locations onboard.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Add Location</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body"><div className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div></div> :
         <div className="table-container">
           <table><thead><tr><th>Location Name</th><th>Description</th></tr></thead>
             <tbody>{data.map(l => <tr key={l.id}><td><strong>{l.location_name}</strong></td><td>{l.description || '—'}</td></tr>)}</tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreateLocationModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); onRefresh(); }} />}
    </div>
  );
}

function CreateLocationModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ location_name: '', description: '' });
  const handleSubmit = async (e) => {
    e.preventDefault();
    try { await api.post('/inventory/locations', form); onCreated(); }
    catch (e) { console.error(e); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Add Location</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Location Name *</label><input className="form-input" value={form.location_name} onChange={e => setForm({ ...form, location_name: e.target.value })} required /></div>
            <div className="form-group"><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Critical Spares
function CriticalSparesTab({ data, loading }) {
  return (
    <div className="card">
      {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
       data.length === 0 ? <div className="empty-state"><AlertTriangle /><h4>No critical spares tracked</h4></div> :
       <div className="table-container">
         <table>
           <thead><tr><th>Part #</th><th>Part Name</th><th>Current</th><th>Min Required</th><th>Location</th><th>Status</th><th>Open Req</th></tr></thead>
           <tbody>
             {data.map(s => (
               <tr key={s.id}>
                 <td><strong>{s.part_number}</strong></td>
                 <td>{s.part_name}</td>
                 <td style={{ fontWeight: 600, color: s.stock_status === 'Zero' ? 'var(--danger)' : 'var(--warning)' }}>{s.current_quantity}</td>
                 <td>{s.minimum_stock}</td>
                 <td>{s.location_name || '—'}</td>
                 <td><span className={`badge ${s.stock_status === 'Zero' ? 'badge-critical' : s.stock_status === 'Low' ? 'badge-warning' : 'badge-healthy'}`}>{s.stock_status}</span></td>
                 <td>{s.open_req_status ? <span className="badge badge-info">{s.open_req_status}</span> : '—'}</td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>
      }
    </div>
  );
}

// Defects
function DefectsTab({ data, loading, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Track equipment defects and failures.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Raise Defect</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Defect #</th><th>Description</th><th>Equipment</th><th>Severity</th><th>Status</th><th>Raised</th></tr></thead>
             <tbody>
               {data.map(d => (
                 <tr key={d.id}>
                   <td>{d.defect_number}</td>
                   <td><strong>{d.description}</strong></td>
                   <td>{d.component_name || '—'}</td>
                   <td><span className={`badge badge-${d.severity.toLowerCase()}`}>{d.severity}</span></td>
                   <td><span className={`badge ${d.status === 'Open' ? 'badge-open' : d.status === 'Resolved' ? 'badge-closed' : 'badge-pending'}`}>{d.status}</span></td>
                   <td>{d.raised_date}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreateDefectModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); onRefresh(); }} />}
    </div>
  );
}

function CreateDefectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ description: '', severity: 'Medium', component_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/inventory/defects', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Raise Defect</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Description *</label><textarea className="form-textarea" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Severity</label><select className="form-select" value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
                {['Critical','High','Medium','Low'].map(s => <option key={s}>{s}</option>)}
              </select></div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>Raise Defect</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Stock Transfers
function TransfersTab({ data, loading, onRefresh }) {
  return (
    <div className="card">
      {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
       data.length === 0 ? <div className="empty-state"><ArrowLeftRight /><h4>No transfers recorded</h4></div> :
       <div className="table-container">
         <table><thead><tr><th>Part</th><th>From</th><th>To</th><th>Qty</th><th>Date</th><th>Authorised By</th></tr></thead>
           <tbody>{data.map(t => <tr key={t.id}><td>{t.part_name}</td><td>{t.from_location || '—'}</td><td>{t.to_location || '—'}</td><td>{t.quantity}</td><td>{t.transfer_date}</td><td>{t.authorised_by}</td></tr>)}</tbody>
         </table>
       </div>
      }
    </div>
  );
}

// Running Hours
function RunningHoursTab({ data, loading, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ component_id: '', running_hours: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/inventory/running-hours', form); onRefresh(); setShowCreate(false); setForm({ component_id: '', running_hours: '', notes: '' }); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Log running hours for equipment. Counter-based maintenance jobs trigger based on these readings.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Log Running Hours</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         <div className="table-container">
           <table><thead><tr><th>Component</th><th>Running Hours</th><th>Starts/Cycles</th><th>Logged By</th><th>Date</th><th>Notes</th></tr></thead>
             <tbody>{data.map(r => <tr key={r.id}><td><strong>{r.component_name}</strong></td><td>{r.running_hours?.toLocaleString()}</td><td>{r.starts_cycles || '—'}</td><td>{r.logged_by_name}</td><td>{r.logged_date}</td><td>{r.notes || '—'}</td></tr>)}</tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>Log Running Hours</h2><button className="btn btn-outline btn-sm" onClick={() => setShowCreate(false)}>×</button></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group"><label className="form-label">Component *</label>
                  <select className="form-select" value={form.component_id} onChange={e => setForm({ ...form, component_id: e.target.value })} required>
                    <option value="">Select...</option>
                    {data.map(r => <option key={r.id} value={r.component_id}>{r.component_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Running Hours *</label><input className="form-input" type="number" value={form.running_hours} onChange={e => setForm({ ...form, running_hours: parseFloat(e.target.value) })} required /></div>
                <div className="form-group"><label className="form-label">Notes</label><input className="form-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-accent" disabled={submitting}>Log Hours</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
